import * as ApiRequests from '../../api/requests';
import { deriveImageType } from '../../lib/manage-alarms-helpers';
import { AuthHelper, FiltersData, ManageAlarmConfig, OTAImageType } from '../../types';
import { OTAReleaseDetails, OtaUpgradeStatus } from '../../types/device';
import { AppThunk } from '../actions';
import { ManageAlarmsActions } from '../actions/manage-alarms-actions';

export const ManageAlarmsThunks = {
    SaveAlarmFilters: (
        filtersData: FiltersData,
        orgId: string,
        userId: string,
        authHelper: AuthHelper,
        addToast: Function
    ): AppThunk => async (dispatch): Promise<void> => {
        dispatch(ManageAlarmsActions.AlarmFiltersSaveStarted());

        const payload = await ApiRequests.saveAlarmFilters(
            {
                organizationId: orgId,
                userId: userId,
                userPreferences: {
                    locationId: filtersData.locationId,
                    siteId: filtersData.siteId,
                    gatewayId: filtersData.gatewayId,
                    deviceType: filtersData.deviceType,
                    type: filtersData.type,
                    gatewayTypeId: filtersData.gatewayTypeId,
                },
            },
            { authHelper }
        );

        if (payload.ok) {
            dispatch(
                ManageAlarmsActions.AlarmFiltersSaveSucceeded(
                    payload.response.value.message,
                    payload.response.value.data
                )
            );
        } else {
            const errorCode = ApiRequests.extractResponseNotOkCode(payload.response);

            addToast(ApiRequests.extractResponseNotOkMessage(payload.response), 'error');
            if (errorCode === 4001) {
                addToast(ApiRequests.extractResponseNotOkMessage(payload.response), 'error');
            }
            dispatch(ManageAlarmsActions.AlarmFiltersSaveFailed(errorCode.toString()));
        }
    },
    UpdateAlarmFilters: (
        filtersData: FiltersData,
        contextualDataId: string,
        userId: string,
        authHelper: AuthHelper,
        addToast: Function
    ): AppThunk => async (dispatch): Promise<void> => {
        dispatch(ManageAlarmsActions.AlarmFiltersUpdateStarted());

        const payload = await ApiRequests.updateAlarmFilters(
            {
                contextualDataId: contextualDataId,
                userId: userId,
                userPreferences: {
                    locationId: filtersData.locationId,
                    siteId: filtersData.siteId,
                    gatewayId: filtersData.gatewayId,
                    deviceType: filtersData.deviceType,
                    type: filtersData.type,
                    gatewayTypeId: filtersData.gatewayTypeId,
                },
            },
            { authHelper }
        );

        if (payload.ok) {
            dispatch(
                ManageAlarmsActions.AlarmFiltersUpdateSucceeded(
                    payload.response.value.message,
                    payload.response.value.data
                )
            );
        } else {
            const errorCode = ApiRequests.extractResponseNotOkCode(payload.response);

            addToast(ApiRequests.extractResponseNotOkMessage(payload.response), 'error');
            if (errorCode === 4001) {
                addToast(ApiRequests.extractResponseNotOkMessage(payload.response), 'error');
            }
            dispatch(ManageAlarmsActions.AlarmFiltersUpdateFailed(errorCode.toString()));
        }
    },
    DeviceChannelCount: (filtersData: FiltersData, authHelper: AuthHelper, addToast: Function): AppThunk => async (
        dispatch
    ): Promise<void> => {
        dispatch(ManageAlarmsActions.DeviceChannelCountStarted());

        const payload = await ApiRequests.deviceChannelCount(
            {
                siteId: filtersData.siteId,
                gatewayId: filtersData.gatewayId,
                deviceType: filtersData.deviceType,
                deviceId: '',
            },
            { authHelper }
        );

        if (payload.ok) {
            dispatch(ManageAlarmsActions.DeviceChannelCountSucceeded(payload.response.message, payload.response.data));
        } else {
            const errorCode = ApiRequests.extractResponseNotOkCode(payload.response);

            addToast(ApiRequests.extractResponseNotOkMessage(payload.response), 'error');
            if (errorCode === 4001) {
                addToast(ApiRequests.extractResponseNotOkMessage(payload.response), 'error');
            }
            dispatch(ManageAlarmsActions.DeviceChannelCountFailed(errorCode.toString()));
        }
    },

    SaveAlarmConfig: (
        gatewayDeviceId: string,
        alarmConfigurations: ManageAlarmConfig[],
        authHelper: AuthHelper,
        addToast: Function
    ): AppThunk => async (dispatch): Promise<void> => {
        dispatch(ManageAlarmsActions.AlarmConfigSaveStarted());

        const payload = await ApiRequests.saveAlarmConfig(
            {
                gatewayDeviceId: gatewayDeviceId,
                alarmConfigurations: alarmConfigurations,
            },
            { authHelper }
        );

        if (payload.ok) {
            dispatch(ManageAlarmsActions.AlarmConfigSaveSucceeded(payload.response.message, payload.response.data));
        } else {
            const errorCode = ApiRequests.extractResponseNotOkCode(payload.response);

            addToast(ApiRequests.extractResponseNotOkMessage(payload.response), 'error');
            if (errorCode === 4001) {
                addToast(ApiRequests.extractResponseNotOkMessage(payload.response), 'error');
            }
            dispatch(ManageAlarmsActions.AlarmConfigSaveFailed(errorCode.toString()));
        }
    },

    UpdateAlarmConfig: (
        gatewayDeviceId: string,
        alarmConfigurations: any,
        authHelper: AuthHelper,
        addToast: Function
    ): AppThunk => async (dispatch): Promise<void> => {
        dispatch(ManageAlarmsActions.AlarmConfigSaveStarted());

        const payload = await ApiRequests.updateAlarmConfig(
            {
                gatewayDeviceId: gatewayDeviceId,
                alarmConfigurationsToUpdate: alarmConfigurations,
            },
            { authHelper }
        );

        if (payload.ok) {
            dispatch(ManageAlarmsActions.AlarmConfigSaveSucceeded(payload.response.message, payload.response.data));
        } else {
            const errorCode = ApiRequests.extractResponseNotOkCode(payload.response);

            addToast(ApiRequests.extractResponseNotOkMessage(payload.response), 'error');
            if (errorCode === 4001) {
                addToast(ApiRequests.extractResponseNotOkMessage(payload.response), 'error');
            }
            dispatch(ManageAlarmsActions.AlarmConfigSaveFailed(errorCode.toString()));
        }
    },

    StarfishUpgrade: (
        deviceId: string[],
        otaReleaseDetails: OTAReleaseDetails,
        otaUpgradeStatus: OtaUpgradeStatus,
        userId: string,
        authHelper: AuthHelper,
        addToast: Function
    ): AppThunk => async (dispatch): Promise<void> => {
        dispatch(ManageAlarmsActions.AlarmConfigSaveStarted());
        const _imagetype = deriveImageType(otaReleaseDetails, otaUpgradeStatus);
        const _imageCrc = _imagetype === OTAImageType.package ? otaReleaseDetails.signature : otaReleaseDetails.metadata;
        const payload = await ApiRequests.starfishUpgrade(
            {
                devices: deviceId,
                method: 'UpdateFirmware',
                params: {
                    imageName: otaReleaseDetails.version,
                    imageDescription: 'Update',
                    imageType: _imagetype,
                    updateDestination: 'BHAI',
                    imageCrc: _imageCrc,
                    imageTimestamp: '',
                },
                requestedBy: userId,
            },
            { authHelper }
        );

        if (payload.ok) {
            dispatch(ManageAlarmsActions.AlarmConfigSaveSucceeded(payload.response.message, payload.response.data));
        } else {
            const errorCode = ApiRequests.extractResponseNotOkCode(payload.response);

            addToast(ApiRequests.extractResponseNotOkMessage(payload.response), 'error');
            if (errorCode === 4001) {
                addToast(ApiRequests.extractResponseNotOkMessage(payload.response), 'error');
            }
            dispatch(ManageAlarmsActions.AlarmConfigSaveFailed(errorCode.toString()));
        }
    },
};
